import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Wand2, Map, CheckSquare, CalendarClock,
  ArrowRight, ChevronRight, Star, Upload,
  BookOpen, Car, CreditCard, Brain, Compass,
  Shield, Lock, ShieldCheck, Trash2, Check, X,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { useAnalytics, EVENTS, GA4 } from "../hooks/useAnalytics";

const popularGuides = [
  { id: "i-485", code: "I-485", nameKey: "guides.greenCard", descKey: "guides.greenCardDesc", icon: BookOpen, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "i-130", code: "I-130", nameKey: "guides.familyPetition", descKey: "guides.familyPetitionDesc", icon: BookOpen, color: "bg-sky-50 text-sky-700 border-sky-200" },
  { id: "i-765", code: "I-765", nameKey: "guides.workAuth", descKey: "guides.workAuthDesc", icon: BookOpen, color: "bg-teal-50 text-teal-700 border-teal-200" },
  { id: "ss-5", code: "SS-5", nameKey: "guides.ssCard", descKey: "guides.ssCardDesc", icon: CreditCard, color: "bg-slate-50 text-slate-700 border-slate-200" },
  { id: "dmv", code: "DMV", nameKey: "guides.dmvGuide", descKey: "guides.dmvGuideDesc", icon: Car, color: "bg-sky-50 text-sky-700 border-sky-200", link: "/dmv-guide" },
  { id: "dv-lottery", code: "DV", nameKey: "landing.dvLotteryName", descKey: "landing.dvLotteryDesc", icon: Compass, color: "bg-emerald-50 text-emerald-700 border-emerald-200", link: "/green-card-lottery" },
];

const featureIcons = [
  { icon: Wand2, color: "bg-teal-50 text-teal-700" },
  { icon: Brain, color: "bg-sky-50 text-sky-700" },
  { icon: Upload, color: "bg-emerald-50 text-emerald-700" },
  { icon: Map, color: "bg-slate-100 text-slate-700" },
  { icon: CheckSquare, color: "bg-teal-50 text-teal-700" },
  { icon: CalendarClock, color: "bg-sky-50 text-sky-700" },
];

export default function LandingPage() {
  const { t } = useTranslation();
  const lp = useLocalePath();
  const { trackEvent, trackGA4 } = useAnalytics();
  const features = t("landing.features", { returnObjects: true });
  const howSteps = t("landing.howSteps", { returnObjects: true });
  const freeFeatures = t("landing.freeFeatures", { returnObjects: true });
  const proFeatures = t("landing.proFeatures", { returnObjects: true });

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={t("landing.title")} description={t("landing.subtitle")} />

      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24 lg:py-32" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100 mb-6 font-medium">
              {t("landing.badge")}
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-6">
              {t("landing.title")}{" "}
              <span className="gradient-text">{t("landing.titleHighlight")}</span>
            </h1>
            <p className="text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
              {t("landing.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={lp("/auth?mode=register")} data-testid="hero-cta-start">
                <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base"
                  onClick={() => { trackEvent(EVENTS.START_FREE_GUIDE, { source: "landing_hero" }); trackGA4(GA4.CLICK_START_FREE, { source: "landing_hero" }); }}>
                  {t("landing.ctaStart")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to={lp("/guides")} data-testid="hero-cta-guides">
                <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base">
                  {t("landing.ctaBrowse")}
                </Button>
              </Link>
            </div>
            <div className="flex items-center gap-4 mt-8 justify-center">
              <div className="text-sm text-slate-500">
                {t("landing.qualitativeProof")}
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-400" data-testid="hero-disclaimer">
              {t("landing.heroDisclaimer")}
            </p>
          </div>
        </div>
      </section>

      {/* Trust & Data Security */}
      <section className="py-10 md:py-14 bg-slate-900" data-testid="trust-section">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Shield className="h-5 w-5 text-teal-400" />
            <h2 className="text-base md:text-lg font-semibold text-white">{t("trust.title")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="flex items-start gap-3 bg-slate-800/60 rounded-lg p-4 border border-slate-700/50">
              <Lock className="h-4 w-4 text-teal-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-200">{t("trust.encryptedTitle")}</p>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">{t("trust.encryptedDesc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-800/60 rounded-lg p-4 border border-slate-700/50">
              <ShieldCheck className="h-4 w-4 text-teal-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-200">{t("trust.independentTitle")}</p>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">{t("trust.independentDesc")}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-slate-800/60 rounded-lg p-4 border border-slate-700/50">
              <Trash2 className="h-4 w-4 text-teal-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-200">{t("trust.deleteTitle")}</p>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">{t("trust.deleteDesc")} <Link to={lp("/privacy")} className="text-teal-400 hover:text-teal-300 underline underline-offset-2">{t("trust.deleteLink")}</Link></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Guides */}
      <section className="py-16 md:py-24 bg-white" data-testid="popular-guides-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-3">{t("landing.popularLabel")}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              {t("landing.popularTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularGuides.map((guide) => (
              <Link to={lp(guide.link || `/guides/${guide.id}`)} key={guide.id}>
                <Card className={`border hover:shadow-lg transition-all duration-300 cursor-pointer card-hover h-full ${guide.color}`} data-testid={`popular-guide-${guide.id}`}>
                  <CardContent className="p-5">
                    <guide.icon className="h-8 w-8 mb-3" />
                    <p className="text-lg font-bold mb-1">{guide.code}</p>
                    <h3 className="text-sm font-semibold mb-1">{t(guide.nameKey)}</h3>
                    <p className="text-xs opacity-80">{t(guide.descKey)}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-slate-50/50" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-3">{t("landing.howLabel")}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">{t("landing.howTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {Array.isArray(howSteps) && howSteps.map((step, i) => (
              <div key={i} className={`opacity-0 animate-fade-in stagger-${i + 1}`}>
                <div className="text-5xl font-bold text-teal-100 mb-4 font-heading">{step.num}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-base leading-relaxed text-slate-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-3">{t("landing.featLabel")}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">{t("landing.featTitle")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.isArray(features) && features.map((f, i) => {
              const meta = featureIcons[i] || featureIcons[0];
              return (
                <Card key={i} className={`bg-white border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 hover:border-teal-200/50 opacity-0 animate-fade-in stagger-${i + 1}`} data-testid={`feature-card-${i}`}>
                  <CardContent className="p-8">
                    <div className={`h-12 w-12 rounded-xl ${meta.color} flex items-center justify-center mb-5`}>
                      <meta.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{f.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing comparison */}
      <section className="py-16 md:py-24 bg-slate-50/50" data-testid="pricing-preview">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-6">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-3">{t("landing.pricingLabel")}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">{t("landing.pricingTitle")}</h2>
          </div>
          <p className="text-center text-sm text-slate-600 mb-12 max-w-xl mx-auto">
            {t("landing.pricingValue")}
          </p>
          <div className="max-w-3xl mx-auto">
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm" data-testid="pricing-comparison-table">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left p-4 font-semibold text-slate-900">{t("landing.compFeature")}</th>
                    <th className="text-center p-4 font-semibold text-slate-900 w-28">{t("landing.free")}</th>
                    <th className="text-center p-4 font-semibold text-white bg-teal-700 w-28 rounded-tr-none">{t("landing.pro")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(t("landing.compFeatures", { returnObjects: true }) || []).map((feat, i) => (
                    <tr key={`comp-${i}`} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                      <td className="p-4 text-slate-600">{feat}</td>
                      <td className="text-center p-4">
                        {i < 3 ? <Check className="h-4 w-4 text-teal-600 mx-auto" /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}
                      </td>
                      <td className="text-center p-4 bg-teal-50/50">
                        <Check className="h-4 w-4 text-teal-600 mx-auto" />
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t border-slate-200">
                    <td className="p-4 font-semibold text-slate-900">{t("landing.compPrice")}</td>
                    <td className="text-center p-4 font-bold text-slate-900">$0</td>
                    <td className="text-center p-4 font-bold text-teal-700 bg-teal-50/50">$15/mo</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to={lp("/auth?mode=register")}>
                <Button variant="outline" className="font-medium px-8 py-5 text-sm" data-testid="pricing-free-cta"
                  onClick={() => trackGA4(GA4.CLICK_START_FREE, { source: "landing_pricing" })}>
                  Start Free
                </Button>
              </Link>
              <Link to={lp("/pricing")}>
                <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium px-8 py-5 text-sm" data-testid="pricing-pro-cta"
                  onClick={() => trackGA4(GA4.CLICK_START_PREMIUM, { source: "landing_pricing" })}>
                  Get Premium <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-3">{t("landing.faqLabel")}</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">{t("landing.faqTitle")}</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: t("landing.faq1Q"), a: t("landing.faq1A") },
              { q: t("landing.faq2Q"), a: t("landing.faq2A") },
              { q: t("landing.faq3Q"), a: t("landing.faq3A") },
            ].map((faq, i) => (
              <Card key={`faq-${i}`} className="border-slate-200" data-testid={`faq-item-${i}`}>
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-teal-700" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4">{t("landing.ctaTitle")}</h2>
          <p className="text-lg text-teal-100 mb-8">{t("landing.ctaSubtitle")}</p>
          <Link to={lp("/auth?mode=register")}>
            <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg px-8 py-6 text-base" data-testid="cta-button">
              {t("landing.ctaStart")} <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
